import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Users, Package, CreditCard, DollarSign, RefreshCw, Flag } from 'lucide-react';
import { adminAPI } from '../../services/api';

const DashboardTab = () => {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [reportStats, setReportStats] = useState(null);
  const [labelStats, setLabelStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5 * 60 * 1000); // Auto-refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, activityRes, reportRes, labelRes] = await Promise.all([
        adminAPI.getDashboardStats(),
        adminAPI.getRecentActivity({ limit: 10 }),
        adminAPI.getReportStats().catch(() => null),
        adminAPI.getLabelStats().catch(() => null),
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }

      if (activityRes.data.success) {
        setActivity(activityRes.data.data);
      }

      if (reportRes?.data?.success) {
        setReportStats(reportRes.data.data);
      }

      if (labelRes?.data?.success) {
        setLabelStats(labelRes.data.data);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <RefreshCw size={40} className="spin" style={{ color: 'var(--primary)' }} />
        <p style={{ marginTop: '16px', color: 'var(--textSecondary)' }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Dashboard Overview</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </span>
          <button
            onClick={loadData}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Daily action items — reports needing review */}
      {reportStats && reportStats.open > 0 && (
        <Link
          to="/admin/reports"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            background: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: '10px',
            textDecoration: 'none',
            color: '#7c5e10',
          }}
        >
          <Flag size={22} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>
              {reportStats.open} reported listing{reportStats.open !== 1 ? 's' : ''} need{reportStats.open === 1 ? 's' : ''} review
            </div>
            <div style={{ fontSize: '13px' }}>
              {reportStats.pending} pending · {reportStats.reviewing} in review — click to open the moderation queue.
            </div>
          </div>
          <span style={{ fontWeight: 700 }}>→</span>
        </Link>
      )}

      {/* Shipping labels needing attention */}
      {labelStats && labelStats.needsLabel > 0 && (
        <Link
          to="/admin/transactions"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            background: '#EDE7F6',
            border: '1px solid #9b59b6',
            borderRadius: '10px',
            textDecoration: 'none',
            color: '#5b2c83',
          }}
        >
          <span style={{ fontSize: '20px' }}>🏷️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>
              {labelStats.needsLabel} order{labelStats.needsLabel !== 1 ? 's' : ''} need{labelStats.needsLabel === 1 ? 's' : ''} a shipping label
            </div>
            <div style={{ fontSize: '13px' }}>
              {labelStats.failed > 0
                ? `${labelStats.failed} failed (e.g., low Shippo balance). Top up Shippo, then click "Retry Label".`
                : 'Open Transactions to buy the labels.'}
            </div>
          </div>
          <span style={{ fontWeight: 700 }}>→</span>
        </Link>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <StatCard
          icon={<Users size={24} />}
          title="Total Users"
          value={stats?.totalUsers || 0}
          growth={stats?.growth?.newUsers || 0}
          color="var(--primary)"
        />
        <StatCard
          icon={<Package size={24} />}
          title="Total Items"
          value={stats?.totalItems || 0}
          growth={stats?.growth?.newItems || 0}
          color="var(--accent)"
        />
        <StatCard
          icon={<CreditCard size={24} />}
          title="Total Transactions"
          value={stats?.totalTransactions || 0}
          growth={stats?.growth?.newTransactions || 0}
          color="var(--success)"
        />
        <StatCard
          icon={<DollarSign size={24} />}
          title="Platform Revenue"
          value={`$${(stats?.totalRevenue || 0).toFixed(2)}`}
          growth={null}
          color="var(--warning)"
        />
      </div>

      {/* Recent Activity */}
      <div style={{ background: 'var(--cardBackground)', borderRadius: '8px', border: '1px solid var(--border)', padding: '24px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Recent Activity</h2>

        {activity && (
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Recent Users */}
            <div>
              <h3 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--textSecondary)' }}>New Users</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activity.recentUsers?.slice(0, 5).map((user) => (
                  <div
                    key={user._id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px',
                      background: 'var(--background)',
                      borderRadius: '4px',
                    }}
                  >
                    <span>{user.username}</span>
                    <span style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Items */}
            <div>
              <h3 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--textSecondary)' }}>New Listings</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activity.recentItems?.slice(0, 5).map((item) => (
                  <div
                    key={item._id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px',
                      background: 'var(--background)',
                      borderRadius: '4px',
                    }}
                  >
                    <span>{item.title}</span>
                    <span style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>
                      ${item.price}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Transactions */}
            <div>
              <h3 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--textSecondary)' }}>Recent Transactions</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activity.recentTransactions?.slice(0, 5).map((txn) => (
                  <div
                    key={txn._id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px',
                      background: 'var(--background)',
                      borderRadius: '4px',
                    }}
                  >
                    <span>{txn.item?.title || 'Unknown'}</span>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: getStatusColor(txn.status),
                        color: 'white',
                      }}>
                        {txn.status}
                      </span>
                      <span style={{ color: 'var(--textSecondary)', fontSize: '14px' }}>
                        ${txn.amount}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, growth, color }) => {
  const hasGrowth = growth !== null && growth !== undefined;
  const isPositive = growth > 0;

  return (
    <div style={{
      background: 'var(--cardBackground)',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      padding: '20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ color }}>{icon}</div>
        {hasGrowth && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: isPositive ? 'var(--success)' : 'var(--error)',
            fontSize: '14px',
          }}>
            {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            {Math.abs(growth)}
          </div>
        )}
      </div>
      <h3 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>{value}</h3>
      <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--textSecondary)' }}>{title}</p>
      {hasGrowth && (
        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--textSecondary)' }}>
          +{growth} new this month
        </p>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    pending: '#f5a623',
    paid: '#0fa573',
    shipped: '#0076d3',
    delivered: '#0fa573',
    completed: '#0fa573',
    cancelled: '#cc0000',
    refunded: '#f5a623',
  };
  return colors[status] || '#767676';
};

export default DashboardTab;
